import { Pipe, PipeTransform } from '@angular/core';

@Pipe({ name: 'groupByEvent' })
export class GroupByEventPipe implements PipeTransform {
  transform(lines: any[]): any[] {
    if (!Array.isArray(lines)) return [];
    const grouped: { [key: string]: any } = {};
    for (const line of lines) {
      const id = line.id_event;
      const date = line.session?.start_date || '';
      const key = `${id}_${date}`;
      if (!grouped[key]) {
        grouped[key] = {
          event: line.event,
          lines: [],
          eventDate: date
        };
      }
      grouped[key].lines.push(line);
    }
    return Object.values(grouped);
  }
}
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AppSettings } from 'src/app/app-settings';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { ApiSalesService } from 'src/app/services/api-sales.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { OrdersEventService } from 'src/app/services/orders-event.service';
import { ReportsService } from 'src/app/services/reports.service';

@Component({
  selector: 'app-detail-order',
  templateUrl: './detail-order.component.html',
  styleUrls: ['./detail-order.component.scss']
})
export class DetailOrderComponent implements OnInit {

  idOrder!: any;
  order!: any;
  seats: any[] = [];

  fees: number = 0;

  showDiscountDetail: boolean = false;
  discountLines: { name: string; total: number }[] = [];
  // Ids de cart_lines afectadas por un abono (descuento rule_type 'pass')
  abonoLineIds: Set<number> = new Set<number>();
  // Ids de cart_lines que tienen un descuento real (no abono/pass) aplicado
  realDiscountLineIds: Set<number> = new Set<number>();

  // El pedido lo ha generado el alta de abonados (SeasonPassEventBookingService),
  // no una compra: ni tiene transacción en el TPV ni entradas imprimibles.
  isSeasonPassOrder: boolean = false;
  // Abono al que pertenece el pedido. Todas sus reservas son del mismo, así que
  // basta con el de la primera para la cabecera.
  seasonPass: any = null;

  allowCitylok: boolean = false;
  allowRefund: boolean = false;
  now: Date = new Date();
  channelName: string = '';
  urlEventImage: string = AppSettings.MEDIA_URI + '/events_images/';

  // Canales permitidos para devoluciones (Totem y Online)
  // Ajustar estos IDs según los canales reales del sistema
  private readonly REFUNDABLE_CHANNELS = [1, 2]; // 1=Online, 2=Totem (ajustar según sea necesario)

  constructor(private activatedRoute: ActivatedRoute, private router: Router, private orderService: OrdersEventService, private reportService: ReportsService,
    private dialog: MatDialog, private auth: AuthenticationService, private apiSalesService: ApiSalesService) { }

  ngOnInit(): void {
    this.allowCitylok = this.auth.isSuperAdmin();
  }

  goBack() {
    this.router.navigate(['/panel-control/orders']);
  }

  /**
   * El teléfono se guarda sin separadores y con el prefijo pegado (34600521630).
   * Se muestra como +34 600 521 630: los 9 últimos dígitos son el número
   * nacional y lo que sobra por delante, el prefijo de país.
   */
  formatPhone(phone: any): string {
    if (!phone) return '-';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length < 9) return String(phone);
    const national = digits.slice(-9);
    const prefix = digits.slice(0, -9);
    const grouped = national.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
    return prefix ? `+${prefix} ${grouped}` : grouped;
  }

  ngAfterViewInit() {
    this.activatedRoute.queryParams.subscribe(params => {
      let idOrder: number = params.search;
      if (idOrder) {
        this.getOrder(idOrder);
      }
    })
  }

  /**
   * Verifica si el pedido puede ser devuelto según el canal, rol del usuario y fecha
   * - Superadmin: puede devolver Totem y Online de cualquier fecha
   * - Admin: puede devolver Totem y Online solo del mismo día
   * - Otros canales (Efectivo, TPV): no se pueden devolver
   */
  canRefundOrder(): boolean {
    if (!this.order) return false;

    // Verificar que NO sea Efectivo ni TPV
    if (this.channelName === 'Efectivo' || this.channelName === 'TPV') {
      return false;
    }

    // Verificar si el canal está en la lista de canales permitidos
    const isRefundableChannel = this.REFUNDABLE_CHANNELS.includes(this.order.id_channel);
    if (!isRefundableChannel) return false;

    // Superadmin puede devolver cualquier día
    if (this.allowCitylok) return true;

    // Admin solo puede devolver si es del mismo día
    if (this.isAdmin()) {
      return this.isSameDay(this.order.created_at);
    }

    return false;
  }

  /**
   * Verifica si el usuario es admin pero NO superadmin
   */
  private isAdmin(): boolean {
    const roles = this.auth.getProfile()?.roles.map((role: any) => role.name) || [];
    return roles.includes('admin') && !this.auth.isSuperAdmin();
  }

  /**
   * Verifica si la fecha del pedido es del mismo día que hoy
   */
  private isSameDay(orderDate: any): boolean {
    const orderDay = new Date(orderDate).toDateString();
    const today = this.now.toDateString();
    return orderDay === today;
  }

  getOrder(idOrder: any) {
    this.seats = [];
    this.seasonPass = null;
    this.fees = 0;
    this.discountLines = [];
    this.abonoLineIds = new Set<number>();
    this.realDiscountLineIds = new Set<number>();
    this.orderService.getOrdersById(idOrder).subscribe({
      next: (res) => {
        this.order = res.data;
        this.apiSalesService.getAllMethods().subscribe({
          next: (res) => {
            this.channelName = res.data.find((channel: any) => channel.id == this.order.id_channel)?.name || '';
          }
        });
        this.fees = res.data.total_fee;
        this.seats = res.data.cart.cart_lines;
        this.isSeasonPassOrder = this.checkSeasonPassOrder(this.seats);
        this.seasonPass = this.findSeasonPass(this.seats);

        // Agrupar descuentos aplicados por id_discount
        const appliedDiscounts: any[] = res.data.applied_discounts || res.data.cart?.applied_discounts || [];
        const grouped = new Map<number, { name: string; total: number }>();
        appliedDiscounts.forEach((d: any) => {
          // Un abono es un descuento de tipo 'pass'. Marcamos cada línea afectada
          // como abono o como descuento real según el rule_type, para elegir la pill.
          const targetSet = d.rule_type === 'pass' ? this.abonoLineIds : this.realDiscountLineIds;
          (d.affected_bookings || []).forEach((ab: any) => {
            if (ab.id_cart_line != null) {
              targetSet.add(ab.id_cart_line);
            }
          });
          const key = d.id_discount;
          if (grouped.has(key)) {
            grouped.get(key)!.total += d.discount_total || 0;
          } else {
            grouped.set(key, { name: d.public_name || d.name, total: d.discount_total || 0 });
          }
        });
        this.discountLines = Array.from(grouped.values());
      }
    });
  }

  /**
   * Reserva generada automáticamente para un abonado al publicar el evento
   * (SeasonPassEventBookingService). No es una venta: no tiene ticket
   * (`id_ticket` null) ni hash propio —el QR de la puerta es el del abono—,
   * así que ni se imprime ni se devuelve por separado.
   */
  isSeasonPassBooking(booking: any): boolean {
    return !!(booking?.season_pass || booking?.id_season_pass);
  }

  /**
   * Un pedido es "de abonado" cuando todas sus reservas lo son. Se calcula una
   * vez por carga porque decide qué acciones de cabecera se enseñan.
   */
  private checkSeasonPassOrder(lines: any[]): boolean {
    const bookings = (lines || []).reduce((acc: any[], line: any) => acc.concat(line.bookings || []), []);
    return bookings.length > 0 && bookings.every((booking: any) => this.isSeasonPassBooking(booking));
  }

  /**
   * Abono del pedido (el de la primera reserva que lo traiga).
   */
  private findSeasonPass(lines: any[]): any {
    for (const line of lines || []) {
      for (const booking of line.bookings || []) {
        if (booking?.season_pass) {
          return booking.season_pass;
        }
      }
    }
    return null;
  }

  /**
   * Ficha del abono en el panel, para poder saltar del pedido al abonado.
   */
  goToSeasonPass() {
    if (this.seasonPass?.id) {
      this.router.navigate(['/panel-control/sports-passes/passes/edit', this.seasonPass.id]);
    }
  }

  /**
   * Nombre de la entrada. Las reservas de abonado no tienen ticket asociado, así
   * que el nombre sale del propio abono en lugar de dejar la línea en blanco.
   */
  getTicketName(line: any, booking: any): string {
    if (this.isSeasonPassBooking(booking)) {
      return 'Abono de temporada';
    }
    return line?.ticket?.name || 'Entrada';
  }

  /**
   * Texto de la etiqueta de abonado: número de socio y categoría del abono
   * cuando la API los trae.
   */
  getSeasonPassLabel(booking: any): string {
    const seasonPass = booking?.season_pass;
    if (!seasonPass) {
      return 'Abonado';
    }
    const parts = [seasonPass.member_number ? 'Abonado nº ' + seasonPass.member_number : 'Abonado'];
    if (seasonPass.category?.name) {
      parts.push(seasonPass.category.name);
    }
    return parts.join(' · ');
  }

  /**
   * Precio unitario (con descuento + gastos de gestión) de una entrada dentro de una línea.
   * Necesario porque en eventos sin mapa una línea puede agrupar varias entradas (bookings)
   * y line.total representa el total de toda la línea, no el de una entrada individual.
   */
  getBookingUnitPrice(line: any): number {
    const bookingCount = line.bookings?.length || 1;
    const feePerUnit = (line.fee_total ?? line.fee ?? 0) / bookingCount;
    const pricePerUnitWithDiscount = line.price_per_unit_with_discount
      ?? ((line.price_with_discount ?? (line.price - (line.discount_total || 0))) / bookingCount);
    return pricePerUnitWithDiscount + feePerUnit;
  }

  /**
   * Precio original (sin descuento) de una entrada individual dentro de una línea.
   */
  getBookingOriginalUnitPrice(line: any): number {
    const bookingCount = line.bookings?.length || 1;
    return line.price_per_unit ?? ((line.price_original ?? line.price) / bookingCount);
  }

  printSingleA4Ticket(ticket: any) {
    this.getUrlDownloadSigleTickets(ticket.id, 'a4');
  }

  printSigleTicket(ticket: any) {
    this.getUrlDownloadSigleTickets(ticket.id, 'ticket');
  }

  getUrlDownloadSigleTickets(ticket: any, type: any) {
    this.reportService.getUrlsDownload(this.order.id).subscribe({
      next: (res) => {
        const url = res.data;
        let param = type == 'a4' ? 'ticket_a4' : 'ticket';
        let uriDownload = `${url[param]}?booking_ids[]=${ticket}`;
        window.open(uriDownload, '_blank');
        this.openInfoCard('Se ha descargado el ticket correctamente.', false, false);
      }, error: (err) => {
        this.openInfoCard('Lo sentimos, no se han podido descargar las entradas.', true, false);
      }
    })
  }

  refundOrder() {
    // Solo superadmin puede elegir devolver gastos de gestión
    // Admin siempre devuelve sin gastos de gestión
    if (this.allowCitylok) {
      const dialogRefFees = this.dialog.open(InfoCardComponent, {
        panelClass: 'info-dialog',
        disableClose: true,
        data: {
          icon: true,
          text: '¿Deseas devolver también los gastos de gestión?',
          btnCancel: true,
          warning: false
        }
      });

      dialogRefFees.afterClosed().subscribe(refundFees => {
        if (refundFees !== null && refundFees !== undefined) {
          this.proceedWithFullRefund(refundFees);
        }
      });
    } else {
      // Admin: proceder sin gastos de gestión
      this.proceedWithFullRefund(false);
    }
  }

  private proceedWithFullRefund(refundFees: boolean) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      disableClose: true,
      data: {
        icon: true,
        text: '¿Estás seguro de que quieres devolver el pedido? Esta acción no podrá deshacerse.',
        btnCancel: true,
        warning: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = refundFees ? { refund_fees: true } : {};
        this.reportService.cancelOrder(this.allowCitylok, this.order.id, payload).subscribe({
          next: (url) => {
            this.openInfoCard('Se ha devuelto el pedido correctamente.', false, false, true);
          }, error: (err) => {
            this.openInfoCard('Lo sentimos, no se ha podido devolver el pedido, vuelve a intentarlo más tarde.', true, false);
          }
        })
      }
    });
  }

  refundTicket(ticket: any) {
    // Solo superadmin puede elegir devolver gastos de gestión
    // Admin siempre devuelve sin gastos de gestión
    if (this.allowCitylok) {
      const dialogRefFees = this.dialog.open(InfoCardComponent, {
        panelClass: 'info-dialog',
        disableClose: true,
        data: {
          icon: true,
          text: '¿Deseas devolver también los gastos de gestión?',
          btnCancel: true,
          warning: false
        }
      });

      dialogRefFees.afterClosed().subscribe(refundFees => {
        if (refundFees !== null && refundFees !== undefined) {
          this.proceedWithPartialRefund(ticket, refundFees);
        }
      });
    } else {
      // Admin: proceder sin gastos de gestión
      this.proceedWithPartialRefund(ticket, false);
    }
  }

  private proceedWithPartialRefund(ticket: any, refundFees: boolean) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      disableClose: true,
      data: {
        icon: true,
        text: '¿Estás seguro de que quieres devolver la entrada seleccionada? Esta acción no podrá deshacerse.',
        btnCancel: true,
        warning: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = { booking_ids: [ticket.id], refund_fees: refundFees };
        this.reportService.cancelOrder(this.allowCitylok, this.order.id, payload).subscribe({
          next: (url) => {
            this.openInfoCard('La entrada se ha devuelto correctamente.', false, false, true);
          }, error: (err) => {
            this.openInfoCard('Lo sentimos, no se ha podido cancelar la entrada, vuelve a intentarlo más tarde.', true, false);
          }
        })
      }
    });
  }

  openConfirm(msg?: string, close?: boolean): void {
    let data = {
      icon: true,
      text: msg,
      btnCancel: true
    };
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: data
    });
    // dialogRef.afterClosed().subscribe((result) => {
    //   if (result && close) {
    //     this.location.back();
    //   }
    // });
  }

  openPrintA4() {
    this.downloadTicketOrder(this.order.id, 'a4');
  }

  openPrintTicket() {
    this.downloadTicketOrder(this.order.id, 'ticket');
  }

  downloadTicketOrder(idOrder: number, type: string) {
    this.reportService.getUrlsDownload(idOrder).subscribe({
      next: (res) => {
        let param = type == 'a4' ? 'ticket_a4' : 'ticket';
        let uriDownload = res.data[param];
        window.open(uriDownload, '_blank');
        this.openInfoCard('Se ha descargado el ticket correctamente.', false, false);
      }, error: (err) => {
        this.openInfoCard('Lo sentimos, no se han podido descargar las entradas.', true, false);
      }
    })
  }

  openInfoCard(msg: string, disabledClose: boolean = false, icon: boolean = true, refund: boolean = false) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: ['info-dialog'],
      disableClose: disabledClose,
      data: {
        icon: icon,
        text: msg,
        btnCancel: false
      }
    });
    if (refund) {
      dialogRef.afterClosed().subscribe({
        next: (res) => {
          this.getOrder(this.order.id);
        }
      })
    }
  }

}
